/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';
import keysToConstants from 'js/lib/keysToConstants';

import { LoadingSection } from '@coursera/cds-core';

import ComparisonTable from 'bundles/coursera-plus/components/prePurchaseUpsell/ComparisonTable';

import _t from 'i18n!nls/coursera-plus';

export const allPlansProductsValues = keysToConstants(['SINGLE_PROGRAM', 'PLUS_MONTHLY', 'PLUS_ANNUAL']);
export type AllPlansProductsType = (typeof allPlansProductsValues)[keyof typeof allPlansProductsValues];
export type SelectProductActionType = Dispatch<SetStateAction<AllPlansProductsType>>;

const getFeatures = ({ isGatewayCertificate }: { isGatewayCertificate: boolean }) => {
  const additionalProps = !isGatewayCertificate
    ? {
        'Coursera Coach': _t('Coursera Coach'),
      }
    : ({} as Record<string, string>);

  return {
    Price: _t('Price'),
    'Free trial': _t('Free trial'),
    'Subscription length': _t('Subscription length'),
    'Learning Programs': _t('Learning Programs'),
    'Completion certificates': _t('Completion certificates'),
    ...additionalProps,
  };
};

const getCourseraPlusProductsPrice = ({
  monthlyPrice,
  annualPrice,
  hasCourseraPlusFreeTrial,
  isGatewayCertificate,
}: {
  monthlyPrice: React.ReactNode;
  annualPrice: React.ReactNode;
  hasCourseraPlusFreeTrial: boolean;
  isGatewayCertificate: boolean;
}) => [
  {
    id: allPlansProductsValues.PLUS_MONTHLY,
    name: _t('Plus monthly'),
    features: {
      Price: monthlyPrice,
      'Free trial': hasCourseraPlusFreeTrial,
      'Subscription length': _t('1 month'),
      'Learning Programs': _t('10,000+'),
      'Completion certificates': _t('Unlimited'),
      'Coursera Coach': !isGatewayCertificate,
    },
  },
  {
    id: allPlansProductsValues.PLUS_ANNUAL,
    name: _t('Plus annual'),
    features: {
      Price: annualPrice,
      'Free trial': hasCourseraPlusFreeTrial,
      'Subscription length': _t('1 year'),
      'Learning Programs': _t('10,000+'),
      'Completion certificates': _t('Unlimited'),
      'Coursera Coach': !isGatewayCertificate,
    },
  },
];

const getSingProgramPrice = ({
  singleProgramPrice,
  hasSingleProgramFreeTrial,
}: {
  singleProgramPrice: React.ReactNode;
  hasSingleProgramFreeTrial: boolean;
}) => [
  {
    id: allPlansProductsValues.SINGLE_PROGRAM,
    name: _t('Single Program'),
    features: {
      Price: singleProgramPrice,
      'Free trial': hasSingleProgramFreeTrial,
      'Subscription length': _t('1 month'),
      'Learning Programs': _t('1'),
      'Completion certificates': _t('1'),
      'Coursera Coach': false,
    },
  },
];

const getProducts = ({
  monthlyPrice,
  annualPrice,
  singleProgramPrice,
  hasSingleProgramFreeTrial = false,
  hasCourseraPlusFreeTrial = true,
  isGatewayCertificate = false,
}: {
  monthlyPrice: React.ReactNode;
  annualPrice: React.ReactNode;
  singleProgramPrice?: React.ReactNode;
  hasSingleProgramFreeTrial?: boolean;
  hasCourseraPlusFreeTrial: boolean;
  isGatewayCertificate: boolean;
}) =>
  singleProgramPrice
    ? [
        ...getSingProgramPrice({ singleProgramPrice, hasSingleProgramFreeTrial }),
        ...getCourseraPlusProductsPrice({ monthlyPrice, annualPrice, hasCourseraPlusFreeTrial, isGatewayCertificate }),
      ]
    : getCourseraPlusProductsPrice({ monthlyPrice, annualPrice, hasCourseraPlusFreeTrial, isGatewayCertificate });

type Props = {
  isStandaloneCourse: boolean;
  showSingleProgram: boolean;
  hasS12nFreeTrial?: boolean;
  isGatewayCertificate: boolean;
  hasCourseraPlusFreeTrial: boolean;
  s12nPriceDisplay?: React.JSX.Element | null;
  standaloneCoursePriceDisplay?: React.JSX.Element;
  courseraPlusMonthlyPriceDisplay: React.JSX.Element | null;
  courseraPlusAnnualPriceDisplay: React.JSX.Element | null;
  setSelectedProduct: SelectProductActionType;
  defaultSelectedProduct: AllPlansProductsType;
};
export const AllPlansPricingTable = ({
  isStandaloneCourse,
  showSingleProgram,
  hasS12nFreeTrial,
  isGatewayCertificate = false,
  hasCourseraPlusFreeTrial = true,
  standaloneCoursePriceDisplay,
  s12nPriceDisplay,
  courseraPlusMonthlyPriceDisplay,
  courseraPlusAnnualPriceDisplay,
  setSelectedProduct,
  defaultSelectedProduct,
}: Props) => {
  const singleProgramPrice = isStandaloneCourse ? standaloneCoursePriceDisplay : s12nPriceDisplay;
  const isLoading = !(
    courseraPlusMonthlyPriceDisplay &&
    courseraPlusAnnualPriceDisplay &&
    (showSingleProgram ? singleProgramPrice : true)
  );

  if (isLoading) {
    return <LoadingSection label={_t('Loading products...')} />;
  }

  const singleProgramPriceWithMonth = <FormattedMessage message={_t('{price}/mo')} price={singleProgramPrice} />;
  const courseraPlusMonthlyPriceWithMonth = (
    <FormattedMessage message={_t('{price}/mo')} price={courseraPlusMonthlyPriceDisplay} />
  );
  const hasSingleProgramFreeTrial = isStandaloneCourse ? false : Boolean(hasS12nFreeTrial);

  const products = showSingleProgram
    ? getProducts({
        monthlyPrice: courseraPlusMonthlyPriceWithMonth,
        annualPrice: courseraPlusAnnualPriceDisplay,
        singleProgramPrice: singleProgramPriceWithMonth,
        hasCourseraPlusFreeTrial,
        hasSingleProgramFreeTrial,
        isGatewayCertificate,
      })
    : getProducts({
        monthlyPrice: courseraPlusMonthlyPriceWithMonth,
        annualPrice: courseraPlusAnnualPriceDisplay,
        hasCourseraPlusFreeTrial,
        isGatewayCertificate,
      });
  const features = getFeatures({ isGatewayCertificate });

  return (
    <ComparisonTable
      products={products}
      features={features}
      defaultSelectedProduct={defaultSelectedProduct}
      onSelect={setSelectedProduct}
    />
  );
};

export default AllPlansPricingTable;
